import React from 'react';
import { ShieldCheckIcon, PrinterIcon } from './Icons';
import { sounds } from '../utils/audio';

export default function PrivacyPolicySection() {
  const handlePrint = () => {
    sounds.playSound('tap');
    window.print();
  };

  return (
    <section id="privacy" className="py-12 lg:py-20 bg-slate-50 text-slate-800 antialiased relative">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        
        {/* Header */}
        <div className="border-b border-slate-200 pb-6 mb-8 flex flex-wrap items-center justify-between gap-4">
          <div>
            <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-emerald-100 text-emerald-800 font-heading font-bold text-xs uppercase tracking-wider mb-2">
              <ShieldCheckIcon className="w-4 h-4 text-emerald-600" />
              <span>Official App Privacy Policy</span>
            </div>
            <h1 className="text-3xl sm:text-4xl font-heading font-black text-slate-900 tracking-tight">
              Privacy Policy
            </h1>
            <p className="text-xs sm:text-sm text-slate-500 font-semibold mt-1">
              <strong>Kiki and Kids: Learn Maths</strong> &bull; Last updated: October 17, 2026
            </p>
          </div>

          <button
            onClick={handlePrint}
            className="btn-bouncy px-4 py-2 rounded-2xl border-2 border-slate-200 bg-white hover:bg-slate-100 text-slate-700 font-heading font-bold text-xs flex items-center gap-1.5 shadow-xs"
          >
            <PrinterIcon className="w-4 h-4 text-slate-500" />
            <span>Print Policy</span>
          </button>
        </div>

        {/* Our Promise Callout */}
        <div className="rounded-3xl bg-emerald-50 border-2 border-emerald-200 p-6 sm:p-8 mb-8 space-y-2">
          <h2 className="font-heading font-bold text-xl text-emerald-950 flex items-center gap-2">
            <span>🛡️</span>
            <span>Our promise</span>
          </h2>
          <p className="text-sm sm:text-base text-emerald-900 font-medium leading-relaxed">
            <strong>Kiki and Kids: Learn Maths</strong> is a maths app for children aged 4 to 9. We do not collect, sell or share personal information about children or parents. The app has no ads, no tracking and no third-party analytics.
          </p>
        </div>

        {/* Full Policy Content Card */}
        <div className="bg-white rounded-3xl border border-slate-200 p-6 sm:p-10 shadow-sm space-y-8 text-sm sm:text-base text-slate-700 leading-relaxed font-normal">
          
          <div className="space-y-3">
            <h3 className="font-heading font-black text-xl text-slate-900">
              What the app stores on your device
            </h3>
            <p>
              To let your child continue where they left off, the app keeps some information only on your iPad:
            </p>
            <ul className="list-disc pl-5 space-y-1.5 text-slate-600 font-medium">
              <li>A first name or nickname that a grown-up types in</li>
              <li>The cartoon buddy picked as an avatar and an age group (4–5, 6–7 or 8–9)</li>
              <li>Stars, stickers, pets, decorations and game progress</li>
              <li>How many minutes were played each day, used for the daily limit and the weekly report in the Parent zone</li>
              <li>Your settings, such as the daily play limit and sound options</li>
            </ul>
            <p className="text-xs sm:text-sm text-slate-500 italic mt-2">
              This information is never sent to us or to anyone else. We cannot see it. It is removed when you delete a player, reset progress in the Parent zone, or delete the app.
            </p>
          </div>

          <div className="space-y-3 pt-4 border-t border-slate-100">
            <h3 className="font-heading font-black text-xl text-slate-900">
              What we do not collect
            </h3>
            <p>
              We do not collect names, email addresses, photos, voice recordings, contacts, precise or approximate location, advertising identifiers, device identifiers, or usage analytics. The app does not use the camera or microphone. It does not use accounts or log-ins.
            </p>
          </div>

          <div className="space-y-3 pt-4 border-t border-slate-100">
            <h3 className="font-heading font-black text-xl text-slate-900">
              Internet use
            </h3>
            <p>
              The app works offline. The only time it uses the internet is a short check, when it opens, to see whether a newer version of the app is required. It downloads one small public file from a web server hosted by GitHub Pages (a service of GitHub, Inc.). No information about your child or your device is sent by the app. As with any web request, the hosting service may see the device’s IP address in its server logs. We do not receive or use that information. GitHub’s own privacy statement explains how they handle such logs:{' '}
              <a
                href="https://docs.github.com/en/site-policy/privacy-policies/github-general-privacy-statement"
                target="_blank"
                rel="noopener noreferrer"
                className="text-kiki-blue underline font-bold"
              >
                GitHub Privacy Statement
              </a>. If the check fails, nothing happens and your child can keep playing.
            </p>
          </div>

          <div className="space-y-3 pt-4 border-t border-slate-100">
            <h3 className="font-heading font-black text-xl text-slate-900">
              Notifications
            </h3>
            <p>
              If a grown-up turns on the optional daily reminder, the app schedules a notification on the device. It is created and shown locally. No notification service of ours is involved, and no information leaves the device.
            </p>
          </div>

          <div className="space-y-3 pt-4 border-t border-slate-100">
            <h3 className="font-heading font-black text-xl text-slate-900">
              Children’s privacy
            </h3>
            <p>
              The app is designed for children and is not directed at collecting information from them. Because we do not collect personal information, we do not need to ask for parental consent for data collection, and there is nothing for us to share or delete on our side. It follows Apple’s Kids Category rules: no ads, no third-party analytics, no links out of the app without a grown-ups check. Settings, the Parent zone and App Store links are behind a check that a young child cannot pass by accident (press and hold, then type a number written in words).
            </p>
          </div>

          <div className="space-y-3 pt-4 border-t border-slate-100">
            <h3 className="font-heading font-black text-xl text-slate-900">
              Third parties
            </h3>
            <p>
              The app does not include any advertising, analytics or social media software from other companies. The only outside service is the one version-check file described above.
            </p>
          </div>

          <div className="space-y-3 pt-4 border-t border-slate-100">
            <h3 className="font-heading font-black text-xl text-slate-900">
              Backups
            </h3>
            <p>
              Apple may back up the information stored on your iPad through iCloud or your computer, depending on your device settings. That is controlled by you and Apple, not by us. See{' '}
              <a
                href="https://www.apple.com/legal/privacy/"
                target="_blank"
                rel="noopener noreferrer"
                className="text-kiki-blue underline font-bold"
              >
                Apple’s privacy information
              </a>.
            </p>
          </div>

          <div className="space-y-3 pt-4 border-t border-slate-100">
            <h3 className="font-heading font-black text-xl text-slate-900">
              Your choices and control
            </h3>
            <ul className="list-disc pl-5 space-y-1.5 text-slate-600 font-medium">
              <li><strong>See progress:</strong> open the Parent zone to see the weekly report.</li>
              <li><strong>Change or delete:</strong> in the Parent zone you can change an age group, delete a player, or reset one player’s progress.</li>
              <li><strong>Delete everything:</strong> delete the app from your iPad.</li>
            </ul>
          </div>

          <div className="space-y-3 pt-4 border-t border-slate-100">
            <h3 className="font-heading font-black text-xl text-slate-900">
              Changes to this policy
            </h3>
            <p>
              If we change this policy, we will update the date at the top. If a change affects children’s information, we will tell parents inside the app before it takes effect.
            </p>
          </div>

          {/* Contact Details */}
          <div className="p-5 bg-slate-50 rounded-2xl border border-slate-200 space-y-1.5 pt-4">
            <h3 className="font-heading font-black text-lg text-slate-900">
              Contact
            </h3>
            <p className="text-sm text-slate-700">
              Questions about privacy? Email{' '}
              <a href="mailto:admin@kikiandkids.com" className="text-kiki-blue underline font-bold">
                admin@kikiandkids.com
              </a>.
            </p>
            <p className="text-xs font-bold text-slate-500 pt-1">
              Kiki and kids , Bangalore India
            </p>
          </div>

        </div>

      </div>
    </section>
  );
}
